import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Smartphone, ShieldCheck, Check, X, Trash2, Pencil } from 'lucide-react'
import Header from '../components/Header'
import { showToast } from '../components/Toast'
import { useI18n } from '../i18n/useI18n'
import { api, ApiError } from '../services/api'
import { getDeviceId } from '../lib/deviceId'

/**
 * 登録端末 自己管理画面 (#841).
 *
 * push/poll に依存せず、ユーザーが **いつでも** 申請中の triage と
 * 承認済み端末の棚卸し/解除をできる場所。新端末側の block(待機 modal=
 * 強制)は #819 enforcement として併存し、本画面は trusted 側 triage を
 * 「画面」に移すもの(memory: 通知取り逃し前提を構造的に回避)。
 *
 * - 申請中(trusted!=true & !deniedAt): 承認 / 拒否(凍結)
 * - 承認済み(trusted): 解除(soft untrust。**最後の trusted は backend
 *   が 409 で拒否** = #837 全ロックアウト再発防止)
 * 操作 authz は backend が trusted-caller を強制(未信頼 caller は 403)。
 */
type FirestoreTimestampLike = { _seconds: number; _nanoseconds?: number }

function isFirestoreTimestamp(v: unknown): v is FirestoreTimestampLike {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { _seconds?: unknown })._seconds === 'number'
  )
}

type Device = {
  deviceId?: string
  id?: string
  deviceLabel?: string | null
  customLabel?: string | null
  trusted?: boolean
  deniedAt?: unknown
  firstSeen?: unknown
  /** #1940 最終利用。ログインのたびに更新される */
  lastSeen?: unknown
  lastIpAddress?: string
}

export default function DeviceManagement() {
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  // #1114 E: 端末名の任意編集。
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')

  const myId = (() => {
    try {
      return getDeviceId()
    } catch {
      return ''
    }
  })()

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.listDevices()
      setDevices(res.devices || [])
    } catch (e) {
      console.error('[DeviceManagement] list failed:', e)
      showToast({ type: 'error', message: t('deviceApproval.actionError') })
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    refresh()
  }, [refresh])

  const idOf = (d: Device) => d.deviceId || d.id || ''

  const formatTs = (ts: unknown): string => {
    if (!ts) return '-'
    try {
      let date: Date
      if (typeof ts === 'string') date = new Date(ts)
      else if (isFirestoreTimestamp(ts)) date = new Date(ts._seconds * 1000)
      else return '-'
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString(locale)
    } catch {
      return '-'
    }
  }

  const runAction = async (key: string, fn: () => Promise<unknown>) => {
    if (busyId) return
    setBusyId(key)
    try {
      await fn()
      await refresh()
    } finally {
      setBusyId(null)
    }
  }

  const handleApprove = (target: string) =>
    runAction(`a:${target}`, async () => {
      try {
        await api.approveDevice(target, myId)
        showToast({ type: 'success', message: t('deviceApproval.approvedToast') })
      } catch {
        showToast({ type: 'error', message: t('deviceApproval.actionError') })
      }
    })

  const handleDeny = (target: string) => {
    if (!window.confirm(t('deviceApproval.denyConfirm'))) return
    return runAction(`d:${target}`, async () => {
      try {
        await api.denyDevice(target, myId)
        showToast({ type: 'success', message: t('deviceApproval.deniedToast') })
      } catch {
        showToast({ type: 'error', message: t('deviceApproval.actionError') })
      }
    })
  }

  // #1107 follow-up: ゴミ箱 = **hard delete**(soft revoke だと承認待ちに
  // 再出現し「削除したのに承認を聞かれる」挙動になるため)。最後の trusted
  // は backend が 409。
  const handleDelete = (target: string) => {
    if (!window.confirm(t('deviceManage.deleteConfirm'))) return
    return runAction(`x:${target}`, async () => {
      try {
        await api.deleteDevice(target, myId)
        showToast({ type: 'success', message: t('deviceManage.deleteDone') })
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          showToast({ type: 'error', message: t('deviceManage.deleteLastTrusted') })
        } else {
          showToast({ type: 'error', message: t('deviceApproval.actionError') })
        }
      }
    })
  }

  // #1114 E: 端末名の保存。
  const handleRename = (target: string) => {
    // **実行中なら何もしない。** runAction は busyId が立っていると黙って
    // 早期 return するので、先に editingId を消すと入力が消えるだけになる
    if (busyId) return
    const label = editLabel.trim()
    setEditingId(null)
    return runAction(`n:${target}`, async () => {
      try {
        await api.renameDevice(target, myId, label)
        showToast({ type: 'success', message: t('deviceManage.renameDone') })
      } catch {
        showToast({ type: 'error', message: t('deviceApproval.actionError') })
      }
    })
  }

  const pending = devices.filter((d) => d.trusted !== true && !d.deniedAt)
  const trusted = devices.filter((d) => d.trusted === true)
  // 承認/拒否/解除 は trusted 端末からのみ(#819 授権の核 = backend 403)。
  // この画面を開いている端末自身が trusted でなければ操作不可なので、
  // 操作ボタンを出さず明示説明する(403 の生エラーを起こさせない)。
  const currentIsTrusted = devices.some(
    (d) => idOf(d) === myId && d.trusted === true,
  )

  // device-id 短縮コード = UA が同一でも端末を一意に識別できる唯一の鍵。
  const shortCode = (d: Device) => idOf(d).slice(0, 8) || '????????'

  /**
   * #1940 **コンポーネントとして定義せず、関数として呼ぶ。**
   *
   * `const Row = (...) => ...` を `<Row ...>` で使うと、親が再描画されるたびに
   * 関数の identity が変わり、React が型の違う要素として **subtree ごと
   * 作り直す**。名前の入力欄がここにあるので、**1 文字打つたびに input が
   * 作り直され、日本語入力の変換が中断される**。関数として呼べばコンポーネント
   * 境界が無くなり、ふつうに差分更新される。
   */
  const renderRow = (d: Device, children: React.ReactNode) => {
    const isThis = idOf(d) === myId && myId !== ''
    const id = idOf(d)
    const displayName = d.customLabel || `${t('deviceManage.deviceNoun')} ${shortCode(d)}`
    return (
      <div key={id} className="bg-white rounded-2xl shadow-card p-4">
        <div className="flex items-start gap-3">
          <Smartphone size={20} className="text-yokoso-red shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            {/* #1114 E: 端末名(任意編集)を主表示。編集中はインライン入力。 */}
            {editingId === id ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  value={editLabel}
                  maxLength={60}
                  onChange={(e) => setEditLabel(e.target.value)}
                  onKeyDown={(e) => {
                    // IME の変換を確定する Enter で保存しない (半端な文字列が入る)
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleRename(id)
                  }}
                  placeholder={t('deviceManage.renamePlaceholder')}
                  className="flex-1 min-w-0 h-8 px-2 text-sm font-body border border-gray-300 rounded-lg focus:outline-none focus:border-yokoso-red"
                />
                <button
                  onClick={() => handleRename(id)}
                  className="h-8 px-2 rounded-lg bg-yokoso-red text-white text-xs font-bold tap-effect"
                >
                  {t('deviceManage.renameSave')}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-body text-sm font-bold text-text-primary truncate">
                  {displayName}
                </span>
                {currentIsTrusted && (
                  <button
                    onClick={() => { setEditingId(id); setEditLabel(d.customLabel || '') }}
                    className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 tap-effect"
                    aria-label="rename device"
                  >
                    <Pencil size={12} className="text-text-tertiary" />
                  </button>
                )}
                {isThis && (
                  <span className="text-[10px] font-display font-bold px-2 py-0.5 rounded-full bg-matcha/15 text-matcha">
                    {t('deviceManage.thisDevice')}
                  </span>
                )}
              </div>
            )}
            <code className="font-mono text-[10px] text-text-tertiary mt-0.5 block">
              ID {shortCode(d)}
            </code>
            <p className="font-body text-xs text-text-tertiary mt-0.5">
              {t('deviceApproval.seenLabel')}: {formatTs(d.firstSeen)}
            </p>
            {/* #1940 最終利用。初回検出だけだと「自分は使っていないのに
                最近使われている」に気付けない */}
            <p className="font-body text-xs text-text-tertiary mt-0.5">
              {t('deviceApproval.lastSeenLabel')}: {formatTs(d.lastSeen)}
            </p>
            {d.lastIpAddress ? (
              <p className="font-body text-xs text-text-tertiary mt-0.5">
                {t('deviceApproval.ipLabel')}: {d.lastIpAddress}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">{children}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-20">
      <Header title={t('deviceManage.title')} onBack={() => navigate(-1)} />

      <div className="px-4 pt-4 space-y-6">
        {loading ? (
          <p className="text-center py-8 font-body text-sm text-text-tertiary">
            ...
          </p>
        ) : devices.length === 0 ? (
          <div className="text-center py-12">
            <Smartphone size={40} className="mx-auto text-text-tertiary mb-2" />
            <p className="font-body text-sm text-text-tertiary">
              {t('deviceManage.empty')}
            </p>
          </div>
        ) : (
          <>
            {!currentIsTrusted && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs font-body text-amber-900 leading-relaxed">
                  {t('deviceManage.notTrustedHere')}
                </p>
              </div>
            )}

            {pending.length > 0 && (
              <div className="space-y-2">
                <p className="font-display text-sm font-bold text-text-primary">
                  {t('deviceManage.pendingSection')}
                </p>
                {/* #1114 F: 拒否(deny)は「凍結」の強い操作であると明示。 */}
                <p className="font-body text-[11px] text-text-tertiary leading-relaxed">
                  {t('deviceManage.pendingHint')}
                </p>
                {pending.map((d) => renderRow(d, (<>
                    {currentIsTrusted && (
                      <>
                        <button
                          onClick={() => handleApprove(idOf(d))}
                          disabled={busyId !== null}
                          className="h-9 px-3 flex items-center gap-1 rounded-full bg-yokoso-red text-white text-xs font-display font-bold tap-effect disabled:opacity-50"
                        >
                          <Check size={14} />
                          {t('deviceApproval.approveAction')}
                        </button>
                        <button
                          onClick={() => handleDeny(idOf(d))}
                          disabled={busyId !== null}
                          className="h-9 px-3 flex items-center gap-1 rounded-full border border-gray-300 text-text-secondary text-xs font-display font-bold tap-effect disabled:opacity-50"
                        >
                          <X size={14} />
                          {t('deviceApproval.denyAction')}
                        </button>
                      </>
                    )}
                  </>)))}
              </div>
            )}

            <div className="space-y-2">
              <p className="font-display text-sm font-bold text-text-primary">
                {t('deviceManage.trustedSection')}
              </p>
              {/* #1114 F: 削除(delete)は「整理」の安全な操作であると明示
                  (パスキー不変・最後の1台は消せない)。deny との違いを説明。 */}
              <p className="font-body text-[11px] text-text-tertiary leading-relaxed">
                {t('deviceManage.trustedHint')}
              </p>
              {trusted.length === 0 ? (
                <p className="font-body text-xs text-text-tertiary">
                  {t('deviceManage.empty')}
                </p>
              ) : (
                trusted.map((d) => renderRow(d, (<>
                    {currentIsTrusted && (
                      <button
                        onClick={() => handleDelete(idOf(d))}
                        disabled={busyId !== null}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 tap-effect disabled:opacity-50"
                        aria-label="delete device"
                      >
                        <Trash2 size={14} className="text-yokoso-red" />
                      </button>
                    )}
                  </>)))
              )}
            </div>

            <div className="flex items-center gap-2 pt-2 text-text-tertiary">
              <ShieldCheck size={14} className="shrink-0" />
              <p className="font-body text-[11px] leading-relaxed">
                {t('deviceManage.note')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
